import { useRef, useState, useMemo } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

// 🧭 Left Panel
import EventDisplay from "../components/CommonComponents/EventDisplay";
import AnnouncementDisplay from "../components/CommonComponents/AnnouncementDisplay";
import ImageDisplay from "../components/CommonComponents/ImageDisplay";
import PrayerGrid from "../components/second_screen/PrayerGrid";
import bottomLogo from "../assets/ConnectUmmah.png";
import FooterStrip from "../components/second_screen/FooterStrip";

// 🌤 Right Panel
import SunInfo from "../components/second_screen/SunInfo";
import CurrentTime from "../components/second_screen/CurrentTimeDisplay";
import IqamahCountdown from "../components/second_screen/NextIqamahCountdown";
import JummahInfo from "../components/second_screen/JummahInfo";

// Import Utility
import { getNextPrayerOrJummah } from "../utils/prayerUtils";

const SecondScreen = ({ 
  colorSettings = {}, 
  prayerData, 
  timezone, 
  currentTime,
  moduleData: currentOuter, 
  innerModule: currentInner, 
  announcements 
}) => {
  const {
    headerBackgroundColor = "#E6E9EE",
    footerBackgroundColor = "#E6E9EE",
    footerTextColor = "#000",
  } = colorSettings;

  const [masjidTimeZone, setMasjidTimeZone] = useState(null);
  const outerNodeRef = useRef(null);
  const innerNodeRef = useRef(null);

  // 1. Calculate REAL Next Event
  const nextEvent = useMemo(() => {
    if (!prayerData || !timezone || !currentTime) return null;
    return getNextPrayerOrJummah(currentTime, prayerData.iqamatimes, prayerData.jummahTimes, 0);
  }, [currentTime, prayerData, timezone]);

  // ✅ FIX: Jummah Mode only on explicitly Jummah events or Friday Dhuhr overlap
  const isJummahMode = useMemo(() => {
    if (!prayerData?.iqamatimes || !currentTime) return false;

    // If the next event is Jummah, kill grid highlights (since grid has no Jummah row)
    if (nextEvent?.type === 'jummah') return true;

    // Optional: If you want to kill highlights specifically during the Dhuhr time on Friday
    // even if the next event says "Asr".
    // Note: If you leave this false, "Asr" will be highlighted during Friday Dhuhr time (which is usually fine).
    // If you want NOTHING highlighted during Friday Dhuhr, keep this logic enabled:
    /*
    const isFriday = currentTime.day() === 5;
    if (isFriday) {
       const dateStr = currentTime.format("YYYY-MM-DD");
       const dhuhrStr = prayerData.iqamatimes.dhuhr || prayerData.iqamatimes.zuhr;
       const asrStr = prayerData.iqamatimes.asr;
       if (dhuhrStr && asrStr) {
          const dhuhrTime = moment.tz(`${dateStr} ${dhuhrStr}`, "YYYY-MM-DD h:mm A", timezone);
          const asrTime = moment.tz(`${dateStr} ${asrStr}`, "YYYY-MM-DD h:mm A", timezone);
          if (currentTime.isSameOrAfter(dhuhrTime) && currentTime.isBefore(asrTime)) {
              return true;
          }
       }
    }
    */
    
    return false;
  }, [currentTime, prayerData, nextEvent]);

  // Loading Guard
  if (!prayerData || !timezone || !currentTime) {
    return <div className="w-full h-screen bg-white" />;
  }

  const innerName = currentInner?.source_Module_Name?.toLowerCase();
  const innerData = currentInner?.data;
  const innerKey = currentInner?.id || "no-inner";

  // ---- Inner Module Transition ----
  const renderInnerModule = () => {
    return (
      <SwitchTransition mode="out-in">
        <CSSTransition key={innerKey} nodeRef={innerNodeRef} timeout={500} classNames="fade" unmountOnExit>
          <div ref={innerNodeRef} className="w-full h-full transition-all duration-500 ease-in-out">
            {(() => {
                if (innerName?.includes("event") && innerData) return <EventDisplay event={innerData[0]} />;
                if (innerName === "announcements" && innerData?.length > 0) return <AnnouncementDisplay announcement={innerData[0]} />;
                if (innerName?.includes("image") && innerData?.length > 0) return <ImageDisplay image={innerData[0]} />;
                return null;
            })()}
          </div>
        </CSSTransition>
      </SwitchTransition>
    );
  };

  // ---- Outer Module Renderer ----
  const renderOuterModule = () => {
    return (
        <div className="flex flex-col min-h-[100dvh] w-full overflow-x-hidden">
          <div className="flex flex-wrap sm:flex-nowrap w-full h-[65vh] md:h-[75vh] lg:h-[85vh] xl:h-screen">
            
            {/* LEFT PANEL */}
            <div className="w-full sm:w-[70%] h-full flex flex-col items-center justify-center shadow-md" style={{ backgroundColor: headerBackgroundColor }}>
              {renderInnerModule()}
              <div className="flex flex-wrap sm:flex-nowrap w-full h-auto mt-0 mb-0 p-0">
                <div className="w-screen h-full">
                  <div className="w-full overflow-hidden">
                    
                    {/* ✅ Pass isJummahMode Flag */}
                    <PrayerGrid
                      colorSettings={colorSettings}
                      prayerData={prayerData}
                      masjidTimeZone={masjidTimeZone}
                      nextEvent={nextEvent}
                      isJummahMode={isJummahMode} 
                    />

                    <div className="w-full shadow-sm" style={{ backgroundColor: footerBackgroundColor, color: footerTextColor }}>
                      <FooterStrip logo={bottomLogo} altText="Connect Ummah" colorSettings={colorSettings} announcements={announcements} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="w-full sm:w-[30%] h-full flex flex-col justify-between bg-[#E6E9EE] shadow-md py-[2vh] gap-[1vh]">
              <SunInfo className="w-full flex-shrink-0" prayerData={prayerData} />
              <CurrentTime className="w-full flex-1 flex flex-col justify-center" prayerData={prayerData} onTimezoneChange={setMasjidTimeZone} />
              <IqamahCountdown className="w-full flex-1 flex flex-col justify-center" prayerData={prayerData} timezone={timezone} />
              <JummahInfo className="w-full flex-shrink-0" prayerData={prayerData} timezone={timezone} currentTime={currentTime} />
            </div>
          </div>
        </div>
    );
  };

  const isOuterFullScreen = currentOuter && !currentOuter.parent_Id;

  return (
    <div className="w-full min-h-screen bg-white text-[clamp(14px,1.5vw,18px)] font-system overflow-hidden">
      <SwitchTransition mode="out-in">
        <CSSTransition key={currentOuter?.id || "no-outer"} nodeRef={outerNodeRef} timeout={500} classNames="fade" unmountOnExit>
          <div ref={outerNodeRef} className="flex flex-col min-h-screen">
            {isOuterFullScreen ? (
              <div className="flex-1 w-full h-full flex items-center justify-center bg-white overflow-hidden">
                {renderOuterModule()}
              </div>
            ) : (
              renderOuterModule()
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
};

export default SecondScreen;